import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { clientService } from "../services/apiService.jsx";
import "./Dashboard.css";

const UpdateClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const formRef = useRef();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  
  const [formData, setFormData] = useState({
    customer_logo1: null,
    customer_joiningDate: "",
    customer_name: "",
    customer_type: "",
    location: "",
    account_manager_mail: "",
    web_address: "",
    address: "",
    billing_address: "",
    contact_person: "",
    cp_position: "",
    cp_contact_num: "",
    cp_mail: "",
    status: "Active",
    existing_logo: ""
  });

  useEffect(() => {
    if (id) {
      fetchClientDetails();
    }
  }, [id]);

  const fetchClientDetails = async () => {
    setLoading(true);
    try {
      const response = await clientService.getClientById(id);
      console.log("Client Details Response:", response);

      let clientData = null;
      if (response?.data?.data) {
        clientData = response.data.data;
      } else if (response?.data) {
        clientData = response.data;
      } else if (response?.success && response?.data) {
        clientData = response.data;
      }

      if (clientData) {
        const formatDate = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";
          return date.toISOString().split("T")[0];
        };

        setFormData({
          customer_logo1: null,
          customer_joiningDate: formatDate(clientData.customer_joiningDate),
          customer_name: clientData.customer_name || "",
          customer_type: clientData.customer_type || "",
          location: clientData.location || "",
          account_manager_mail: clientData.account_manager_mail || "",
          web_address: clientData.web_address || "",
          address: clientData.address || "",
          billing_address: clientData.billing_address || "",
          contact_person: clientData.contact_person || "",
          cp_position: clientData.cp_position || "",
          cp_contact_num: clientData.cp_contact_num || "",
          cp_mail: clientData.cp_mail || "",
          status: clientData.status || "Active",
          existing_logo: clientData.customer_logo1 || ""
        });
      } else {
        setError("Failed to load client details.");
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError("Failed to load client details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const copyAddressToBilling = (e) => {
    if (e.target.checked) {
      setFormData(prev => ({
        ...prev,
        billing_address: prev.address
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Only Company Name is mandatory
    if (!formData.customer_name) errors.customer_name = "Company name is required";
    
    // Optional field validations - only validate format if value is provided
    if (formData.account_manager_mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.account_manager_mail)) {
      errors.account_manager_mail = "Enter a valid email address";
    }
    
    if (formData.cp_contact_num && !/^\d{10}$/.test(formData.cp_contact_num)) {
      errors.cp_contact_num = "Phone number must be 10 digits";
    }
    
    if (formData.cp_mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cp_mail)) {
      errors.cp_mail = "Enter a valid email address";
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fix the validation errors");
      return;
    }
    
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key !== 'existing_logo' && formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
          formDataToSend.append(key, formData[key]);
        }
      });

      console.log("Sending update data:", Object.fromEntries(formDataToSend));

      const response = await clientService.updateClient(id, formDataToSend);
      console.log("Update Client Response:", response);
      
      setSuccess("Client updated successfully!");
      
      setTimeout(() => {
        navigate("/client");
      }, 2000);
      
    } catch (error) {
      console.error("Error updating client:", error);
      
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.errors) {
            setValidationErrors(errorData.errors);
          } else {
            setError(errorData.message || "Validation error. Please check your inputs.");
          }
        } else if (error.response.status === 401) {
          setError("You are not authorized. Please login again.");
        } else if (error.response.status === 403) {
          setError("You don't have permission to update clients.");
        } else if (error.response.status === 404) {
          setError("Client not found.");
        } else {
          setError(error.response.data?.message || "Failed to update client. Please try again.");
        }
      } else if (error.request) {
        setError("No response from server. Please check your internet connection.");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex align-items-center" style={{ marginBottom: '0.5rem', marginTop: '0.25rem' }}>
          <button
            onClick={() => navigate("/client")}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: '1.6rem', lineHeight: 1, textDecoration: 'none' }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 500 }}>Update Client</h2>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading client details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess("")} style={{ fontSize: '0.8rem' }}></button>
        </div>
      )}
      
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert" style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
          {error}
          <button type="button" className="btn-close" onClick={() => setError("")} style={{ fontSize: '0.8rem' }}></button>
        </div>
      )}

      {/* Header with back button and title */}
      <div className="d-flex align-items-center" style={{ marginBottom: '0.5rem', marginTop: '0.25rem' }}>
        <button
          onClick={() => navigate("/client")}
          className="btn btn-link text-dark p-0 me-2"
          style={{ fontSize: '1.6rem', lineHeight: 1, textDecoration: 'none' }}
          title="Go back to clients list"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 500 }}>Update Client</h2>
      </div>

      {/* Client name subtitle */}
      <p className="text-muted" style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
        Updating: {formData.customer_name}
      </p>
      
      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card" style={{ marginTop: 0, border: '1px solid #dee2e6', borderRadius: '6px' }}>
              <div className="card-body" style={{ padding: '0.75rem' }}>
                <form
                  className="row g-1"
                  id="clientForm"
                  ref={formRef}
                  noValidate
                  encType="multipart/form-data"
                  method="post"
                  onSubmit={handleSubmit}
                >
                  <div className="col-md-3">
                    <label htmlFor="inputPhoto" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Upload Logo
                    </label>
                    {formData.existing_logo && (
                      <div className="mb-1">
                        <img
                          src={formData.existing_logo}
                          alt="Current Logo"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                          }}
                          className="img-thumbnail"
                        />
                        <small className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>Current</small>
                      </div>
                    )}
                    <input
                      name="customer_logo1"
                      type="file"
                      className={`form-control form-control-sm ${validationErrors.customer_logo1 ? 'is-invalid' : ''}`}
                      id="inputPhoto"
                      accept="image/*"
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Leave empty to keep current</small>
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputcustJoiningDate" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Joining Date
                    </label>
                    <input
                      type="date"
                      name="customer_joiningDate"
                      className={`form-control form-control-sm ${validationErrors.customer_joiningDate ? 'is-invalid' : ''}`}
                      id="inputcustJoiningDate"
                      value={formData.customer_joiningDate}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputCompanyname" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Company Name <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      className={`form-control form-control-sm ${validationErrors.customer_name ? 'is-invalid' : ''}`}
                      id="inputCompanyname"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.customer_name && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.customer_name}</div>
                    )}
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputLocation" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      className={`form-control form-control-sm ${validationErrors.location ? 'is-invalid' : ''}`}
                      id="inputLocation"
                      value={formData.location}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputAccountEmail" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Account Manager Email
                    </label>
                    <input
                      type="email"
                      name="account_manager_mail"
                      className={`form-control form-control-sm ${validationErrors.account_manager_mail ? 'is-invalid' : ''}`}
                      id="inputAccountEmail"
                      value={formData.account_manager_mail}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.account_manager_mail && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.account_manager_mail}</div>
                    )}
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputWebaddress" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Web Address
                    </label>
                    <input
                      type="url"
                      name="web_address"
                      className={`form-control form-control-sm ${validationErrors.web_address ? 'is-invalid' : ''}`}
                      id="inputWebaddress"
                      value={formData.web_address}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputCustomerType" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Customer Type
                    </label>
                    <input
                      type="text"
                      name="customer_type"
                      className={`form-control form-control-sm ${validationErrors.customer_type ? 'is-invalid' : ''}`}
                      id="inputCustomerType"
                      value={formData.customer_type}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="inputStatus" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Status
                    </label>
                    <select
                      name="status"
                      className={`form-select form-select-sm ${validationErrors.status ? 'is-invalid' : ''}`}
                      id="inputStatus"
                      value={formData.status}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Inactive clients and their projects drop out of the main project lists.</small>
                  </div>

                  <div className="col-md-6">
                    <label htmlFor="inputAddress" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      className={`form-control form-control-sm ${validationErrors.address ? 'is-invalid' : ''}`}
                      id="inputAddress"
                      value={formData.address}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-6">
                    <label htmlFor="inputBillingaddress" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Billing Address
                    </label>
                    <input
                      type="text"
                      name="billing_address"
                      className={`form-control form-control-sm ${validationErrors.billing_address ? 'is-invalid' : ''}`}
                      id="inputBillingaddress"
                      value={formData.billing_address}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-12">
                    <div className="form-check" style={{ marginTop: '0.25rem' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="copyAddress"
                        onChange={copyAddressToBilling}
                        style={{ marginTop: '0.2rem' }}
                      />
                      <label className="form-check-label" htmlFor="copyAddress" style={{ fontSize: '0.8rem' }}>
                        Copy Address to Billing Address
                      </label>
                    </div>
                  </div>
                  
                  <h6 style={{ margin: '0.35rem 0 0.15rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Contact Details</h6>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputContactperson" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Contact Person
                    </label>
                    <input
                      type="text"
                      name="contact_person"
                      className={`form-control form-control-sm ${validationErrors.contact_person ? 'is-invalid' : ''}`}
                      id="inputContactperson"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputPosition" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Position
                    </label>
                    <input
                      type="text"
                      name="cp_position"
                      className={`form-control form-control-sm ${validationErrors.cp_position ? 'is-invalid' : ''}`}
                      id="inputPosition"
                      value={formData.cp_position}
                      onChange={handleInputChange}
                      maxLength="100"
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputPhoneNumber" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="cp_contact_num"
                      className={`form-control form-control-sm ${validationErrors.cp_contact_num ? 'is-invalid' : ''}`}
                      id="inputPhoneNumber"
                      value={formData.cp_contact_num}
                      onChange={handleInputChange}
                      pattern="[0-9]{10}"
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.cp_contact_num && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.cp_contact_num}</div>
                    )}
                  </div>
                  
                  <div className="col-md-3">
                    <label htmlFor="inputClientEmail" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Email ID
                    </label>
                    <input
                      type="email"
                      name="cp_mail"
                      className={`form-control form-control-sm ${validationErrors.cp_mail ? 'is-invalid' : ''}`}
                      id="inputClientEmail"
                      placeholder="Enter a valid email"
                      value={formData.cp_mail}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.cp_mail && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.cp_mail}</div>
                    )}
                  </div>
                  
                  <div className="col-12 d-flex justify-content-end gap-2" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/client")}
                      style={{ padding: '0.2rem 0.8rem', fontSize: '0.8rem' }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isSubmitting}
                      style={{ padding: '0.2rem 0.8rem', fontSize: '0.8rem' }}
                    >
                      {isSubmitting ? "Updating..." : "Update Client"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default UpdateClient;